import type { SignalMetadataFieldDistributionEntry } from "@repo/app-types/entities/signal";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";

export const NULL_METADATA_PLACEHOLDER = "\u2014";

export type DateRangeDays = 30 | 60 | 90;

export const dateRangeOptions: readonly SimpleSelectOption<DateRangeDays>[] = [
  { label: "Last 30 days", value: 30 },
  { label: "Last 60 days", value: 60 },
  { label: "Last 90 days", value: 90 },
];

export function computePercent(count: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((count / total) * 100);
}

export function computeAndFormatPercent(count: number, total: number): string {
  const percent = computePercent(count, total);
  return `${percent === 0 && count > 0 ? "<1" : percent}%`;
}

export function formatMetadataFieldKey(fieldKey: string): string {
  const spaced = fieldKey.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const standardFormatter = new Intl.NumberFormat("en-US");

export function formatCompactNumber(value: number): string {
  if (Math.abs(value) >= 10_000) {
    return compactFormatter.format(value);
  }
  return standardFormatter.format(value);
}

/**
 * Format an extracted metadata value for display: numbers use the compact
 * formatter, nulls render as a placeholder, strings pass through.
 */
export function formatMetadataValue(value: string | number | null): string {
  if (value === null) return NULL_METADATA_PLACEHOLDER;
  if (typeof value === "number") return formatCompactNumber(value);
  return value;
}

/**
 * Truncate `n` to `significantDigits` significant digits without rounding
 * up (floor). Avoids bumping a value to the next bucket boundary when
 * formatting the lower edge.
 * e.g. (1.2345, 3) → 1.23; (0.004567, 3) → 0.00456
 */
function truncateSig(n: number, significantDigits: number): number {
  if (n === 0) return 0;
  const magnitude = Math.floor(Math.log10(Math.abs(n)));
  const pow = Math.pow(10, significantDigits - 1 - magnitude);
  return Math.floor(n * pow) / pow;
}

/**
 * Format a number compactly with up to `significantDigits` significant
 * digits. Uses truncation to avoid rounding up to the next bucket boundary.
 * e.g. 999,999 → "999K"; 1,500,000 → "1.5M"; 1,234,567 → "1.23M"
 */
export function compactNumber(n: number, significantDigits = 3): string {
  if (n < 1_000) return String(n);
  if (n < 1_000_000) {
    return `${truncateSig(n / 1_000, significantDigits)}K`;
  }
  return `${truncateSig(n / 1_000_000, significantDigits)}M`;
}

/**
 * Format a `[lower, upper)` bucket into a display label. Mirrors the BE
 * formatter that produces these labels today — kept on the FE so
 * presentation lives with the renderer, not the API.
 *   [0, 1)        → "0"
 *   [100, 150)    → "100-149"
 *   [1000, 2000)  → "1K-1.99K"
 */
export function formatBucketRange(lower: number, upper: number): string {
  return upper - lower <= 1
    ? compactNumber(lower)
    : `${compactNumber(lower)}-${compactNumber(upper - 1)}`;
}

/**
 * Display label for a distribution entry — enum value directly, numeric
 * bucket formatted via `formatBucketRange`.
 */
export function formatDistributionEntryLabel(
  entry: SignalMetadataFieldDistributionEntry,
): string {
  if ("value" in entry) {
    return entry.value;
  }
  return formatBucketRange(entry.lower, entry.upper);
}
